/**
 * Handing an email's embedded attachment to the reader as a file.
 *
 * Shared by both email surfaces — deal documents and deal deliverables — because getting the object
 * URL's lifetime wrong is silent, and one correct implementation is cheaper than two that drift.
 */

import type { DeliverableAttachment } from './emlAdapter';

/** Units for {@link formatAttachmentSize}, largest step last. */
const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

/**
 * Formats a byte count for display beside an attachment name.
 * @param {number} bytes - The attachment's size.
 * @returns {string} A short human-readable size, e.g. `12.4 KB`.
 */
export function formatAttachmentSize(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // Whole bytes read oddly with a decimal; everything above is clearer with one.
  return `${unit === 0 ? value : value.toFixed(1)} ${SIZE_UNITS[unit]}`;
}

/** Used when a caller has no name for the file; better than letting the browser invent a bare GUID. */
const FALLBACK_DOWNLOAD_NAME = 'download';

/**
 * Saves a blob to the reader's machine under the given name.
 *
 * ⚠️ THE OBJECT URL IS MINTED HERE, ON CLICK, AND REVOKED IMMEDIATELY — in a `finally`, so a click
 * that throws still releases it. A URL created when a message loads instead survives until the
 * document is discarded, so a message with a dozen attachments leaks a dozen blobs whether or not
 * anyone downloads one, and with a 2.8 MB image that is real memory. Scoping it to the click leaves
 * no lifetime to manage.
 *
 * ⚠️ EXPORTED FOR THE VIEWERS' FAILURE STATES, WHICH ARE NOW A SECOND CALLER. An email that is too
 * large to parse, or that will not read at all, still has to reach the user — on the legacy client
 * Documents page this is the ONLY way out, because routing `.eml` into the preview removed that
 * page's `fileDownload` path and it has no download control of its own. ⛔ Do not write a second
 * object-URL dance somewhere else; the lifetime is the easy thing to get silently wrong.
 * @param {Blob} blob - The bytes to save.
 * @param {string} fileName - The name to save under; blank falls back to a generic name.
 * @returns {void}
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName.trim() === '' ? FALLBACK_DOWNLOAD_NAME : fileName;
    link.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Saves one attachment to the reader's machine.
 *
 * ⚠️ THE REAL MEDIA TYPE MATTERS. Labelling every attachment `application/octet-stream` — which the
 * deal-documents viewer used to do — hands the operating system a file it has been told is opaque
 * binary, so it refuses to preview it and may not associate it with any application. The type is
 * what makes a downloaded `.png` open as a picture.
 * @param {DeliverableAttachment} attachment - The attachment to save.
 * @returns {void}
 */
export function downloadAttachment(attachment: DeliverableAttachment): void {
  // Copied into a fresh array rather than handed over directly: a `Uint8Array` is typed over
  // `ArrayBufferLike`, which admits `SharedArrayBuffer`, and `Blob` accepts only the non-shared
  // kind. The copy is what makes that true rather than asserted.
  downloadBlob(new Blob([new Uint8Array(attachment.content)], { type: attachment.mimeType }), attachment.fileName);
}
